// §7.7.3 Central staff grants management page.
// medflow_admin: full CRUD (list + create + revoke).
// customer_admin: list + revoke own-org grants (server scopes automatically).

import { useState, useCallback } from 'react'
import {
  PageHeader,
  DataTable,
  ColumnSettingsButton,
  Modal,
  colors,
  input,
  btnPrimary,
  btnSecondary,
  btnDanger,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { useApiList } from '../../hooks/use-api-list'
import { useTableColumns } from '../../hooks/use-table-columns'
import { useAuth } from '../../hooks/use-auth'
import { api, getAuthToken } from '../../services/api'

// ── types ─────────────────────────────────────────────────────────────────────

interface StaffGrantRow {
  readonly id: string
  readonly staffUserId: string
  readonly organizationId: string
  readonly environmentId: string | null
  readonly grantedById: string
  readonly grantedAt: string
  readonly expiresAt: string
  readonly status: 'pending' | 'active' | 'expired' | 'revoked'
  readonly reason: string
  readonly revokedAt: string | null
  readonly revokedById: string | null
}

// ── helpers ───────────────────────────────────────────────────────────────────

function formatDate(ts: string | null): string {
  if (!ts) return '—'
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return ts
  }
}

function statusChip(status: StaffGrantRow['status']): JSX.Element {
  const palette: Record<StaffGrantRow['status'], { bg: string; color: string }> = {
    active: { bg: '#dcfce7', color: '#166534' },
    pending: { bg: '#fef9c3', color: '#854d0e' },
    expired: { bg: '#f3f4f6', color: '#6b7280' },
    revoked: { bg: '#fee2e2', color: '#991b1b' },
  }
  const { bg, color } = palette[status] ?? { bg: '#f3f4f6', color: '#6b7280' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '0.75rem',
        fontWeight: 600,
        background: bg,
        color,
      }}
    >
      {status}
    </span>
  )
}

// ── styles ────────────────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  marginBottom: '6px',
}

const fieldStyle: React.CSSProperties = { marginBottom: '16px' }

const errorBoxStyle: React.CSSProperties = {
  marginBottom: '16px',
  padding: '12px 16px',
  background: '#fee2e2',
  border: '1px solid #fca5a5',
  borderRadius: '6px',
  color: '#dc2626',
  fontSize: '0.875rem',
}

// ── component ─────────────────────────────────────────────────────────────────

export function StaffGrantsPage() {
  const { user } = useAuth()
  const isMedflowAdmin = user?.role === 'medflow_admin'
  const { tableId: _, ...tableColumnProps } = useTableColumns('fleet-staff-grants')

  // Filter state
  const [statusFilter, setStatusFilter] = useState('')
  const filters: Record<string, string | undefined> = {
    status: statusFilter || undefined,
  }

  const {
    data: rows,
    total,
    loading,
    error,
    refresh,
  } = useApiList<StaffGrantRow>({
    path: '/admin/staff-grants',
    limit: 50,
    filters,
  })

  // Revoke state
  const [revoking, setRevoking] = useState<string | null>(null)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  // Open (staff-login) state
  const [opening, setOpening] = useState<string | null>(null)
  const [openError, setOpenError] = useState<string | null>(null)

  const handleOpen = useCallback(async (row: StaffGrantRow) => {
    if (!row.environmentId) return
    setOpening(row.id)
    setOpenError(null)

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAuthToken()}`,
    }

    try {
      // Step 1: obtain staff voucher
      const voucherRes = await fetch('/api/auth/staff-voucher', {
        method: 'POST',
        headers,
        body: JSON.stringify({ environmentId: row.environmentId }),
      })
      if (!voucherRes.ok) {
        const err = (await voucherRes.json().catch(() => ({}))) as { error?: string }
        if (voucherRes.status === 403) {
          throw new Error(err.error ?? 'No active grant for this environment.')
        }
        if (voucherRes.status === 503) {
          throw new Error('Emergency rotation is in progress. Staff access is temporarily paused.')
        }
        throw new Error(err.error ?? `Staff voucher failed (${voucherRes.status})`)
      }

      // Step 2: obtain relay session
      const sessionRes = await fetch('/api/remote-access/sessions', {
        method: 'POST',
        headers,
        body: JSON.stringify({ environmentId: row.environmentId }),
      })
      if (!sessionRes.ok) {
        const err = (await sessionRes.json().catch(() => ({}))) as { error?: string }
        throw new Error(err.error ?? `Session failed (${sessionRes.status})`)
      }
      const sessionBody = (await sessionRes.json()) as { data: { token: string; relayUrl: string } }
      const { token, relayUrl } = sessionBody.data

      // Step 3: open relay in new tab
      const url = `${relayUrl}/env/${row.environmentId}/?token=${encodeURIComponent(token)}`
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (err) {
      setOpenError(err instanceof Error ? err.message : 'Failed to open session')
    } finally {
      setOpening(null)
    }
  }, [])

  const handleRevoke = useCallback(
    async (id: string) => {
      setRevoking(id)
      setRevokeError(null)
      try {
        await api.post(`/admin/staff-grants/${id}/revoke`, {})
        refresh()
      } catch (err) {
        setRevokeError(err instanceof Error ? err.message : 'Revoke failed')
      } finally {
        setRevoking(null)
      }
    },
    [refresh],
  )

  // Create-grant modal state
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({
    staffUserId: '',
    organizationId: '',
    environmentId: '',
    ttlDays: 7,
    reason: '',
  })
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  const resetCreateForm = useCallback(() => {
    setCreateForm({ staffUserId: '', organizationId: '', environmentId: '', ttlDays: 7, reason: '' })
    setCreateError(null)
  }, [])

  const handleCreateSubmit = useCallback(async () => {
    setCreating(true)
    setCreateError(null)
    try {
      const body = {
        staffUserId: createForm.staffUserId.trim(),
        organizationId: createForm.organizationId.trim(),
        ...(createForm.environmentId.trim() ? { environmentId: createForm.environmentId.trim() } : {}),
        ttlDays: createForm.ttlDays,
        reason: createForm.reason.trim(),
      }
      await api.post('/admin/staff-grants', body)
      setShowCreate(false)
      resetCreateForm()
      refresh()
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Create failed')
    } finally {
      setCreating(false)
    }
  }, [createForm, refresh, resetCreateForm])

  // ── columns ────────────────────────────────────────────────────────────────

  const columns: readonly Column<StaffGrantRow>[] = [
    {
      key: 'staffUserId',
      header: 'Staff User ID',
      render: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.staffUserId}</span>,
      sortable: true,
    },
    {
      key: 'organizationId',
      header: 'Organization',
      render: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.organizationId}</span>,
      sortable: true,
    },
    {
      key: 'environmentId',
      header: 'Environment',
      render: (r) =>
        r.environmentId ? (
          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.environmentId}</span>
        ) : (
          <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>org-wide</span>
        ),
    },
    {
      key: 'grantedById',
      header: 'Granted By',
      render: (r) => <span style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.grantedById}</span>,
    },
    {
      key: 'grantedAt',
      header: 'Granted At',
      render: (r) => formatDate(r.grantedAt),
      sortable: true,
    },
    {
      key: 'expiresAt',
      header: 'Expires At',
      render: (r) => formatDate(r.expiresAt),
      sortable: true,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => statusChip(r.status),
      sortable: true,
      filterable: true,
    },
    {
      key: 'id',
      header: 'Actions',
      render: (r) => {
        const canRevoke = r.status === 'active' || r.status === 'pending'
        const canOpen = isMedflowAdmin && r.status === 'active' && r.environmentId !== null
        if (!canRevoke && !canOpen) return null
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {canOpen && (
              <button
                style={{ ...btnSecondary, padding: '4px 10px', fontSize: '0.75rem' }}
                disabled={opening === r.id}
                onClick={(e) => {
                  e.stopPropagation()
                  void handleOpen(r)
                }}
                title="Log in to this edge via the relay"
              >
                {opening === r.id ? 'Opening...' : '↗ Open'}
              </button>
            )}
            {canRevoke && (
              <button
                style={{ ...btnDanger, padding: '4px 10px', fontSize: '0.75rem' }}
                disabled={revoking === r.id}
                onClick={() => handleRevoke(r.id)}
              >
                {revoking === r.id ? 'Revoking...' : 'Revoke'}
              </button>
            )}
          </div>
        )
      },
    },
  ]

  const STATUS_OPTIONS = ['', 'active', 'pending', 'expired', 'revoked'] as const

  return (
    <div>
      <PageHeader
        title="Staff Grants"
        subtitle={`Cross-realm MedFlow staff access grants${total > 0 ? ` — ${total.toLocaleString()} grants` : ''}`}
        actions={
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            {isMedflowAdmin && (
              <button style={btnPrimary} onClick={() => setShowCreate(true)}>
                Request Access
              </button>
            )}
          </div>
        }
      />

      <div style={{ padding: '24px' }}>
        {/* Status filter */}
        <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', alignItems: 'flex-end' }}>
          <div>
            <label style={labelStyle}>Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ ...input, width: '160px' }}
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s || '(all)'}
                </option>
              ))}
            </select>
          </div>
          {statusFilter && (
            <button style={btnSecondary} onClick={() => setStatusFilter('')}>
              Clear filter
            </button>
          )}
        </div>

        {/* Revoke error */}
        {revokeError && <div style={errorBoxStyle}>{revokeError}</div>}

        {/* Open error */}
        {openError && <div style={errorBoxStyle}>{openError}</div>}

        {/* List error */}
        {error && <div style={errorBoxStyle}>{error}</div>}

        {/* Table */}
        {loading ? (
          <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>Loading...</div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            keyField="id"
            emptyMessage="No staff grants found."
            {...tableColumnProps}
          />
        )}
      </div>

      {/* Create-grant modal (medflow_admin only) */}
      <Modal
        isOpen={showCreate}
        onClose={() => {
          setShowCreate(false)
          resetCreateForm()
        }}
        title="Request Staff Access"
        width="520px"
      >
        <div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Staff User ID (UUID)</label>
            <input
              type="text"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={createForm.staffUserId}
              onChange={(e) => setCreateForm((f) => ({ ...f, staffUserId: e.target.value }))}
              style={{ ...input, width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Organization ID (UUID)</label>
            <input
              type="text"
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              value={createForm.organizationId}
              onChange={(e) => setCreateForm((f) => ({ ...f, organizationId: e.target.value }))}
              style={{ ...input, width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Environment ID (UUID) — leave blank for org-wide</label>
            <input
              type="text"
              placeholder="optional"
              value={createForm.environmentId}
              onChange={(e) => setCreateForm((f) => ({ ...f, environmentId: e.target.value }))}
              style={{ ...input, width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>TTL (days): {createForm.ttlDays}</label>
            <input
              type="range"
              min={1}
              max={30}
              value={createForm.ttlDays}
              onChange={(e) => setCreateForm((f) => ({ ...f, ttlDays: Number(e.target.value) }))}
              style={{ width: '100%' }}
            />
            <div
              style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: colors.textMuted }}
            >
              <span>1 day</span>
              <span>30 days</span>
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Reason</label>
            <textarea
              placeholder="Brief description of why access is needed"
              value={createForm.reason}
              onChange={(e) => setCreateForm((f) => ({ ...f, reason: e.target.value }))}
              rows={3}
              style={{ ...input, width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>

          {createError && <div style={errorBoxStyle}>{createError}</div>}

          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button
              style={btnSecondary}
              onClick={() => {
                setShowCreate(false)
                resetCreateForm()
              }}
              disabled={creating}
            >
              Cancel
            </button>
            <button style={btnPrimary} onClick={handleCreateSubmit} disabled={creating}>
              {creating ? 'Creating...' : 'Create Grant'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
